import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import api from '../services/api';
import { BACKEND_URL } from '../config';
import { toast } from '../components/Toast';
import { confirm } from '../components/ConfirmDialog';
import DjamannLoader from '../components/DjamannLoader';
import MobileMoneyReceipt from '../components/MobileMoneyReceipt';
import {
  Smartphone, Plus, Search, Trash2, ArrowDownCircle, ArrowUpCircle,
  Send, Receipt, CreditCard, Calendar, Filter, X, Clock, TrendingUp,
  DollarSign, Users, Wallet, ChevronDown, ChevronUp, Printer, Save,
  Lock, Unlock, AlertTriangle, CheckCircle, BarChart3, Settings
} from 'lucide-react';

const TYPE_LABELS = {
  depot: { label: 'Dépôt', icon: ArrowDownCircle, color: 'text-green-600', bg: 'bg-green-50', badge: 'bg-green-100 text-green-700' },
  retrait: { label: 'Retrait', icon: ArrowUpCircle, color: 'text-red-600', bg: 'bg-red-50', badge: 'bg-red-100 text-red-700' },
  transfert: { label: 'Transfert', icon: Send, color: 'text-blue-600', bg: 'bg-blue-50', badge: 'bg-blue-100 text-blue-700' },
  facture: { label: 'Paiement facture', icon: Receipt, color: 'text-purple-600', bg: 'bg-purple-50', badge: 'bg-purple-100 text-purple-700' },
  credit: { label: 'Achat crédit', icon: CreditCard, color: 'text-orange-600', bg: 'bg-orange-50', badge: 'bg-orange-100 text-orange-700' },
};

const TRANSACTION_TYPES = Object.keys(TYPE_LABELS);

const formatMoney = (v) => {
  const num = parseFloat(v) || 0;
  return num.toLocaleString('fr-FR', { minimumFractionDigits: 0 }) + ' F';
};

const BusinessMobileMoney = () => {
  const context = useOutletContext() || {};
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('transactions');

  // Dashboard data
  const [dashboard, setDashboard] = useState(null);
  const [operators, setOperators] = useState([]);

  // Transaction list
  const [transactions, setTransactions] = useState([]);
  const [totalTx, setTotalTx] = useState(0);
  const [filterType, setFilterType] = useState('');
  const [filterOperator, setFilterOperator] = useState('');
  const [searchTx, setSearchTx] = useState('');
  const [dateTx, setDateTx] = useState(new Date().toISOString().split('T')[0]);

  // New transaction form
  const [showForm, setShowForm] = useState(false);
  const [txForm, setTxForm] = useState({
    operator_id: '', type: 'depot', montant: '', commission: '',
    frais_client: '', numero_client: '', numero_destinataire: '', reference: '', notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [autoCommission, setAutoCommission] = useState(0);

  // Caisse
  const [caisse, setCaisse] = useState(null);
  const [dayStats, setDayStats] = useState(null);
  const [caisseForm, setCaisseForm] = useState({ solde_ouverture: '', solde_fermeture: '', notes: '' });
  const [uvForm, setUvForm] = useState({}); // { operatorId: montant_uv }
  const [caisseHistory, setCaisseHistory] = useState([]);

  // Commission rates
  const [commissionRates, setCommissionRates] = useState([]);
  const [rateForm, setRateForm] = useState({
    operator_id: '', type_transaction: 'depot', montant_min: '0', montant_max: '999999999',
    commission_fixe: '0', commission_pourcentage: '0'
  });
  const [showRateForm, setShowRateForm] = useState(false);
  const [receiptTx, setReceiptTx] = useState(null);

  // Settings entreprise (pour impression)
  const [settings, setSettings] = useState(null);

  // Bilan
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportDates, setReportDates] = useState({
    date_debut: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    date_fin: new Date().toISOString().split('T')[0]
  });

  // =====================
  // DATA LOADING
  // =====================

  const loadDashboard = useCallback(async () => {
    try {
      const res = await api.get('/business/mobile-money/dashboard');
      setDashboard(res.data);
      setOperators(res.data.operators || []);
    } catch (err) {
      console.error('Erreur dashboard MM:', err);
    }
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const res = await api.get('/business/settings');
      setSettings(res.data);
    } catch (err) { /* silently fail */ }
  }, []);

  const loadTransactions = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterType) params.append('type', filterType);
      if (filterOperator) params.append('operator_id', filterOperator);
      if (dateTx) { params.append('date_debut', dateTx); params.append('date_fin', dateTx); }
      if (searchTx) params.append('search', searchTx);
      params.append('limit', '100');

      const res = await api.get(`/business/mobile-money/transactions?${params}`);
      setTransactions(res.data.data || []);
      setTotalTx(res.data.total || 0);
    } catch (err) {
      console.error('Erreur chargement transactions:', err);
    }
  }, [filterType, filterOperator, dateTx, searchTx]);

  const loadCaisse = useCallback(async () => {
    try {
      const [closingRes, statsRes, historyRes] = await Promise.all([
        api.get('/business/mobile-money/daily-closing'),
        api.get('/business/mobile-money/stats'),
        api.get('/business/mobile-money/daily-closings'),
      ]);
      setCaisse(closingRes.data);
      setDayStats(statsRes.data);
      setCaisseHistory(historyRes.data || []);
    } catch (err) {
      console.error('Erreur caisse:', err);
    }
  }, []);

  const loadCommissionRates = useCallback(async () => {
    try {
      const res = await api.get('/business/mobile-money/commission-rates');
      setCommissionRates(res.data || []);
    } catch (err) {
      console.error('Erreur barèmes:', err);
    }
  }, []);

  const loadOperators = useCallback(async () => {
    try {
      const res = await api.get('/business/mobile-money/operators');
      setOperators(res.data || []);
    } catch (err) {
      console.error('Erreur opérateurs:', err);
    }
  }, []);

  // Initial load
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([loadDashboard(), loadOperators(), loadSettings()]);
      setLoading(false);
    };
    init();
  }, [loadDashboard, loadOperators, loadSettings]);

  // Load tab-specific data
  useEffect(() => {
    if (tab === 'transactions') loadTransactions();
    else if (tab === 'caisse') loadCaisse();
    else if (tab === 'commissions') loadCommissionRates();
  }, [tab, loadTransactions, loadCaisse, loadCommissionRates]);

  // Auto-calculate commission when form changes
  useEffect(() => {
    const fetchCommission = async () => {
      if (txForm.operator_id && txForm.type && txForm.montant > 0) {
        try {
          const res = await api.get(
            `/business/mobile-money/commission/calculate?operator_id=${txForm.operator_id}&type=${txForm.type}&montant=${txForm.montant}`
          );
          setAutoCommission(res.data.commission || 0);
        } catch { setAutoCommission(0); }
      }
    };
    const timer = setTimeout(fetchCommission, 300);
    return () => clearTimeout(timer);
  }, [txForm.operator_id, txForm.type, txForm.montant]);

  // =====================
  // HANDLERS
  // =====================

  const handleCreateTransaction = async (e) => {
    e.preventDefault();
    if (caisse?.statut !== 'ouvert') {
      toast.error('La caisse doit être ouverte pour enregistrer une transaction');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...txForm,
        montant: parseFloat(txForm.montant),
        commission: txForm.commission !== '' ? parseFloat(txForm.commission) : undefined,
        frais_client: txForm.frais_client ? parseFloat(txForm.frais_client) : 0,
      };
      await api.post('/business/mobile-money/transactions', payload);
      toast.success('Transaction enregistrée !');
      setShowForm(false);
      setTxForm({ operator_id: '', type: 'depot', montant: '', commission: '', frais_client: '', numero_client: '', numero_destinataire: '', reference: '', notes: '' });
      loadTransactions();
      loadDashboard();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors de l\'enregistrement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTransaction = async (txId) => {
    const ok = await confirm('Supprimer cette transaction ?', 'Cette action est irréversible.');
    if (!ok) return;
    try {
      await api.delete(`/business/mobile-money/transactions/${txId}`);
      toast.success('Transaction supprimée');
      loadTransactions();
      loadDashboard();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleOpenCaisse = async () => {
    try {
      // Construire les UV par opérateur
      const operator_uvs = {};
      operators.filter(o => o.actif).forEach(o => {
        operator_uvs[o.id] = parseFloat(uvForm[o.id]) || 0;
      });

      await api.post('/business/mobile-money/daily-closing/open', {
        solde_ouverture: parseFloat(caisseForm.solde_ouverture) || 0,
        operator_uvs,
        notes: caisseForm.notes || null,
      });
      toast.success('Caisse ouverte !');
      loadCaisse();
      setCaisseForm({ solde_ouverture: '', solde_fermeture: '', notes: '' });
      setUvForm({});
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur ouverture caisse');
    }
  };

  const handleCloseCaisse = async () => {
    const ok = await confirm('Fermer la caisse ?', 'Cette action clôture le point de la journée.');
    if (!ok) return;
    try {
      // Construire les UV de fermeture par opérateur
      const operator_uvs = {};
      operators.filter(o => o.actif).forEach(o => {
        operator_uvs[o.id] = parseFloat(uvForm[o.id]) || 0;
      });

      await api.post('/business/mobile-money/daily-closing/close', {
        solde_fermeture: parseFloat(caisseForm.solde_fermeture) || 0,
        operator_uvs,
        notes: caisseForm.notes || null,
      });
      toast.success('Caisse fermée !');
      loadCaisse();
      setCaisseForm({ solde_ouverture: '', solde_fermeture: '', notes: '' });
      setUvForm({});
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur fermeture caisse');
    }
  };

  const handleSaveRate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/business/mobile-money/commission-rates', {
        ...rateForm,
        montant_min: parseFloat(rateForm.montant_min) || 0,
        montant_max: parseFloat(rateForm.montant_max) || 999999999,
        commission_fixe: parseFloat(rateForm.commission_fixe) || 0,
        commission_pourcentage: parseFloat(rateForm.commission_pourcentage) || 0,
      });
      toast.success('Barème enregistré');
      setShowRateForm(false);
      setRateForm({ operator_id: '', type_transaction: 'depot', montant_min: '0', montant_max: '999999999', commission_fixe: '0', commission_pourcentage: '0' });
      loadCommissionRates();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur');
    }
  };

  const handleDeleteRate = async (rateId) => {
    const ok = await confirm('Supprimer ce barème ?');
    if (!ok) return;
    try {
      await api.delete(`/business/mobile-money/commission-rates/${rateId}`);
      toast.success('Barème supprimé');
      loadCommissionRates();
    } catch (err) {
      toast.error('Erreur');
    }
  };

  const loadReport = useCallback(async () => {
    setReportLoading(true);
    try {
      const res = await api.get(`/business/mobile-money/report?date_debut=${reportDates.date_debut}&date_fin=${reportDates.date_fin}`);
      setReport(res.data);
    } catch (err) {
      toast.error('Erreur chargement bilan');
    } finally {
      setReportLoading(false);
    }
  }, [reportDates]);

  // =====================
  // RENDER HELPERS
  // =====================

  if (loading) {
    return <DjamannLoader variant="section" text="Chargement Mobile Money..." />;
  }

  const todayStats = dashboard?.today || {};
  const byOperator = dashboard?.by_operator || [];

  // =====================
  // TABS
  // =====================

  const tabs = [
    { key: 'transactions', label: 'Transactions', icon: Receipt },
    { key: 'caisse', label: 'Point de caisse', icon: Wallet },
    { key: 'commissions', label: 'Commissions', icon: Settings },
    { key: 'bilan', label: 'Bilan', icon: BarChart3 },
  ];

  return (
    <>
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-6">

      {/* ========== HEADER ========== */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Smartphone className="w-7 h-7 text-indigo-600" />
            Mobile Money
          </h1>
          <p className="text-gray-500 text-sm mt-1">Gestion de votre point de transfert</p>
        </div>
        <button
          onClick={() => {
            if (caisse?.statut !== 'ouvert') {
              toast.error('Vous devez d\'abord ouvrir la caisse avant de faire une transaction');
              setTab('caisse');
              return;
            }
            setShowForm(true); setTab('transactions');
          }}
          className={`px-4 py-2.5 rounded-xl flex items-center gap-2 transition font-medium shadow-sm ${
            caisse?.statut === 'ouvert'
              ? 'bg-indigo-600 text-white hover:bg-indigo-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          <Plus size={18} />
          Nouvelle transaction
        </button>
      </div>

      {/* ========== STATS RAPIDES ========== */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={<BarChart3 className="w-5 h-5 text-indigo-600" />} label="Transactions" value={todayStats.nombre_transactions || 0} />
        <StatCard icon={<TrendingUp className="w-5 h-5 text-green-600" />} label="Commissions" value={formatMoney(todayStats.total_commissions)} />
        <StatCard icon={<ArrowDownCircle className="w-5 h-5 text-blue-600" />} label="Dépôts" value={formatMoney(todayStats.total_depots)} />
        <StatCard icon={<ArrowUpCircle className="w-5 h-5 text-red-600" />} label="Retraits" value={formatMoney(todayStats.total_retraits)} />
      </div>

      {/* ========== SOLDES OPÉRATEURS ========== */}
      {byOperator.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
          {byOperator.map(op => (
            <div key={op.operator_id} className="bg-white rounded-xl border p-3 text-center" style={{ borderTopColor: op.couleur, borderTopWidth: 3 }}>
              <p className="text-xs text-gray-500 truncate">{op.nom}</p>
              <p className="text-sm font-bold mt-1">{formatMoney(op.solde_flottant)}</p>
              <p className="text-xs text-gray-400">{op.nombre_transactions} tx</p>
            </div>
          ))}
        </div>
      )}

      {/* ========== ONGLETS ========== */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
        {tabs.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition ${
                tab === t.key ? 'bg-white shadow-sm text-indigo-700' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon size={16} />
              <span className="hidden sm:inline">{t.label}</span>
            </button>
          );
        })}
      </div>

      {/* ========== TAB: TRANSACTIONS ========== */}
      {tab === 'transactions' && (
        <div className="space-y-4">
          {/* Filtres */}
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text" placeholder="Rechercher (numéro, référence...)"
                value={searchTx} onChange={e => setSearchTx(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <input
              type="date" value={dateTx} onChange={e => setDateTx(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
            />
            <select value={filterType} onChange={e => setFilterType(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
              <option value="">Tous types</option>
              {TRANSACTION_TYPES.map(t => <option key={t} value={t}>{TYPE_LABELS[t].label}</option>)}
            </select>
            <select value={filterOperator} onChange={e => setFilterOperator(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
              <option value="">Tous opérateurs</option>
              {operators.filter(o => o.actif).map(o => <option key={o.id} value={o.id}>{o.nom}</option>)}
            </select>
          </div>

          {/* Formulaire nouvelle transaction */}
          {showForm && caisse?.statut === 'ouvert' && (
            <form onSubmit={handleCreateTransaction} className="bg-white border-2 border-indigo-200 rounded-xl p-5 space-y-4">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Plus size={18} className="text-indigo-600" /> Nouvelle transaction
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Opérateur *</label>
                  <select required value={txForm.operator_id} onChange={e => setTxForm({...txForm, operator_id: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500">
                    <option value="">Sélectionner...</option>
                    {operators.filter(o => o.actif).map(o => <option key={o.id} value={o.id}>{o.nom}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                  <select required value={txForm.type} onChange={e => setTxForm({...txForm, type: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500">
                    {TRANSACTION_TYPES.map(t => <option key={t} value={t}>{TYPE_LABELS[t].label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Montant (F CFA) *</label>
                  <input type="number" required min="1" value={txForm.montant}
                    onChange={e => setTxForm({...txForm, montant: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">N° Client *</label>
                  <input type="text" required value={txForm.numero_client}
                    onChange={e => setTxForm({...txForm, numero_client: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                    placeholder="07 xx xx xx xx"
                  />
                </div>
                {txForm.type === 'transfert' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">N° Destinataire</label>
                    <input type="text" value={txForm.numero_destinataire}
                      onChange={e => setTxForm({...txForm, numero_destinataire: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                      placeholder="07 xx xx xx xx"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Commission <span className="text-gray-400">(auto: {formatMoney(autoCommission)})</span>
                  </label>
                  <input type="number" step="0.01" value={txForm.commission}
                    onChange={e => setTxForm({...txForm, commission: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                    placeholder={`Auto: ${autoCommission}`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Frais client</label>
                  <input type="number" step="0.01" value={txForm.frais_client}
                    onChange={e => setTxForm({...txForm, frais_client: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Référence</label>
                  <input type="text" value={txForm.reference}
                    onChange={e => setTxForm({...txForm, reference: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                    placeholder="ID transaction opérateur"
                  />
                </div>
                <div className="sm:col-span-2 md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <input type="text" value={txForm.notes}
                    onChange={e => setTxForm({...txForm, notes: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                    placeholder="Optionnel"
                  />
                </div>
              </div>
              <div className="flex gap-3 justify-end">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">Annuler</button>
                <button type="submit" disabled={submitting}
                  className="px-5 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium">
                  {submitting ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </form>
          )}

          {/* Liste des transactions */}
          <div className="bg-white rounded-xl border overflow-hidden">
            <div className="px-4 py-3 border-b bg-gray-50 flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700">{totalTx} transaction{totalTx > 1 ? 's' : ''}</span>
            </div>
            {transactions.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <Smartphone className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Aucune transaction pour cette date</p>
              </div>
            ) : (
              <div className="divide-y">
                {transactions.map(tx => {
                  const typeInfo = TYPE_LABELS[tx.type] || TYPE_LABELS.depot;
                  const Icon = typeInfo.icon;
                  return (
                    <div key={tx.id} className="px-4 py-3 flex items-center gap-3 hover:bg-gray-50 group">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${typeInfo.bg}`}>
                        <Icon size={18} className={typeInfo.color} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${typeInfo.badge}`}>{typeInfo.label}</span>
                          <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: tx.operator_couleur + '20', color: tx.operator_couleur }}>
                            {tx.operator_nom}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 mt-0.5">
                          <span className="text-sm text-gray-600">{tx.numero_client}</span>
                          {tx.numero_destinataire && <span className="text-xs text-gray-400">→ {tx.numero_destinataire}</span>}
                          {tx.reference && <span className="text-xs text-gray-400">Réf: {tx.reference}</span>}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-semibold text-gray-900">{formatMoney(tx.montant)}</p>
                        {tx.commission > 0 && <p className="text-xs text-green-600">+{formatMoney(tx.commission)} com.</p>}
                      </div>
                      <div className="text-xs text-gray-400 shrink-0 hidden sm:block">
                        {new Date(tx.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                      <button onClick={() => setReceiptTx(tx)}
                        className="text-indigo-400 hover:text-indigo-600 transition p-1" title="Imprimer reçu">
                        <Printer size={16} />
                      </button>
                      <button onClick={() => handleDeleteTransaction(tx.id)}
                        className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition p-1">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========== TAB: POINT DE CAISSE ========== */}
      {tab === 'caisse' && (
        <div className="space-y-6">
          {/* État de la caisse */}
          <div className="bg-white rounded-xl border p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Wallet size={20} className="text-indigo-600" />
              Caisse du jour — {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </h3>

            {!caisse?.statut ? (
              // Caisse non ouverte
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                <div className="text-center mb-5">
                  <Lock className="w-10 h-10 text-amber-500 mx-auto mb-3" />
                  <p className="text-amber-800 font-medium">La caisse n'est pas encore ouverte</p>
                </div>
                <div className="max-w-2xl mx-auto space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-amber-700 mb-1">💵 Solde cash d'ouverture (F CFA)</label>
                    <input type="number" value={caisseForm.solde_ouverture}
                      onChange={e => setCaisseForm({...caisseForm, solde_ouverture: e.target.value})}
                      className="w-full border border-amber-300 rounded-lg px-3 py-2 text-sm text-center font-semibold focus:ring-2 focus:ring-amber-500"
                      placeholder="Montant cash en caisse"
                    />
                  </div>
                  {operators.filter(o => o.actif).length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-amber-700 mb-2">📱 Soldes UV (flottant) par opérateur</label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {operators.filter(o => o.actif).map(op => (
                          <div key={op.id} className="bg-white rounded-lg border p-3" style={{ borderLeftColor: op.couleur, borderLeftWidth: 3 }}>
                            <label className="block text-xs text-gray-500 mb-1">{op.nom}</label>
                            <input type="number" value={uvForm[op.id] || ''}
                              onChange={e => setUvForm({...uvForm, [op.id]: e.target.value})}
                              className="w-full border rounded px-2 py-1.5 text-sm text-center font-semibold focus:ring-2 focus:ring-amber-500"
                              placeholder="0"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <button onClick={handleOpenCaisse}
                    className="w-full bg-amber-600 text-white px-4 py-2.5 rounded-lg hover:bg-amber-700 font-medium flex items-center justify-center gap-2">
                    <Unlock size={16} />
                    Ouvrir la caisse
                  </button>
                </div>
              </div>
            ) : caisse.statut === 'ouvert' ? (
              // Caisse ouverte
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-green-700 font-medium">Caisse ouverte</span>
                  <span className="text-xs text-gray-400">
                    depuis {caisse.heure_ouverture ? new Date(caisse.heure_ouverture).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '—'}
                  </span>
                </div>

                {dayStats && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500">💵 Solde cash ouverture</p>
                      <p className="font-bold text-lg">{formatMoney(caisse.solde_ouverture)}</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-3">
                      <p className="text-xs text-green-600">Commissions gagnées</p>
                      <p className="font-bold text-lg text-green-700">{formatMoney(dayStats.total_commissions)}</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-3">
                      <p className="text-xs text-blue-600">Transactions</p>
                      <p className="font-bold text-lg text-blue-700">{dayStats.nombre_transactions}</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-3">
                      <p className="text-xs text-purple-600">Volume total</p>
                      <p className="font-bold text-lg text-purple-700">
                        {formatMoney(
                          parseFloat(dayStats.total_depots || 0) +
                          parseFloat(dayStats.total_retraits || 0) +
                          parseFloat(dayStats.total_transferts || 0) +
                          parseFloat(dayStats.total_factures || 0) +
                          parseFloat(dayStats.total_credits || 0)
                        )}
                      </p>
                    </div>
                  </div>
                )}

                {/* UV par opérateur */}
                {caisse.operator_balances && caisse.operator_balances.length > 0 && (
                  <div className="mb-6">
                    <p className="text-sm font-medium text-gray-700 mb-2">📱 UV (flottant) par opérateur</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                      {caisse.operator_balances.map(ob => (
                        <div key={ob.operator_id} className="bg-white border rounded-lg p-3" style={{ borderLeftColor: ob.couleur || '#6B7280', borderLeftWidth: 3 }}>
                          <p className="text-xs text-gray-500 mb-1">{ob.nom_operateur}</p>
                          <p className="font-bold text-sm">{formatMoney(ob.uv_ouverture)}</p>
                          <p className="text-xs text-gray-400 mt-1">ouverture</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Détails par type */}
                {dayStats && (
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
                    {TRANSACTION_TYPES.map(type => {
                      const info = TYPE_LABELS[type];
                      const key = `total_${type}s`;
                      const val = dayStats[key] || 0;
                      return (
                        <div key={type} className={`${info.bg} rounded-lg p-2 text-center`}>
                          <p className="text-xs text-gray-500">{info.label}</p>
                          <p className={`font-semibold ${info.color}`}>{formatMoney(val)}</p>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Fermeture */}
                <div className="border-t pt-4">
                  <p className="text-sm font-medium text-gray-700 mb-3">Fermer la caisse</p>
                  <div className="space-y-4">
                    <div className="flex flex-col sm:flex-row gap-3">
                      <div className="flex-1">
                        <label className="block text-xs text-gray-500 mb-1">💵 Solde cash réel de fermeture (F CFA)</label>
                        <input type="number" value={caisseForm.solde_fermeture}
                          onChange={e => setCaisseForm({...caisseForm, solde_fermeture: e.target.value})}
                          className="w-full border rounded-lg px-3 py-2 text-sm font-semibold focus:ring-2 focus:ring-red-500"
                          placeholder="Comptez votre caisse..."
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs text-gray-500 mb-1">Notes (optionnel)</label>
                        <input type="text" value={caisseForm.notes}
                          onChange={e => setCaisseForm({...caisseForm, notes: e.target.value})}
                          className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500"
                          placeholder="Observations..."
                        />
                      </div>
                    </div>
                    {operators.filter(o => o.actif).length > 0 && (
                      <div>
                        <label className="block text-xs text-gray-500 mb-2">📱 Soldes UV réels de fermeture par opérateur</label>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                          {operators.filter(o => o.actif).map(op => (
                            <div key={op.id} className="bg-white rounded-lg border p-3" style={{ borderLeftColor: op.couleur, borderLeftWidth: 3 }}>
                              <label className="block text-xs text-gray-500 mb-1">{op.nom}</label>
                              <input type="number" value={uvForm[op.id] || ''}
                                onChange={e => setUvForm({...uvForm, [op.id]: e.target.value})}
                                className="w-full border rounded px-2 py-1.5 text-sm text-center font-semibold focus:ring-2 focus:ring-red-500"
                                placeholder="0"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <button onClick={handleCloseCaisse}
                      className="w-full sm:w-auto bg-red-600 text-white px-5 py-2.5 rounded-lg hover:bg-red-700 font-medium flex items-center justify-center gap-2">
                      <Lock size={16} />
                      Fermer la caisse
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              // Caisse fermée
              <div className="bg-gray-50 border rounded-xl p-6">
                <div className="text-center mb-4">
                  <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-3" />
                  <p className="font-medium text-gray-900">Caisse fermée pour aujourd'hui</p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-left">
                  <div>
                    <p className="text-xs text-gray-500">💵 Ouverture cash</p>
                    <p className="font-semibold">{formatMoney(caisse.solde_ouverture)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">💵 Fermeture cash</p>
                    <p className="font-semibold">{formatMoney(caisse.solde_fermeture)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Commissions</p>
                    <p className="font-semibold text-green-600">{formatMoney(caisse.total_commissions)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Écart cash</p>
                    <p className={`font-semibold ${parseFloat(caisse.ecart) === 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatMoney(caisse.ecart)}
                    </p>
                  </div>
                </div>
                {/* UV résumé par opérateur */}
                {caisse.operator_balances && caisse.operator_balances.length > 0 && (
                  <div className="mt-5 border-t pt-4">
                    <p className="text-sm font-medium text-gray-700 mb-3">📱 Bilan UV par opérateur</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                      {caisse.operator_balances.map(ob => {
                        const ecart = parseFloat(ob.ecart_uv || 0);
                        return (
                          <div key={ob.operator_id} className="bg-white border rounded-lg p-3" style={{ borderLeftColor: ob.couleur || '#6B7280', borderLeftWidth: 3 }}>
                            <p className="font-medium text-sm mb-2">{ob.nom_operateur}</p>
                            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                              <span className="text-gray-500">UV ouverture</span>
                              <span className="text-right font-medium">{formatMoney(ob.uv_ouverture)}</span>
                              <span className="text-gray-500">UV théorique</span>
                              <span className="text-right font-medium">{formatMoney(ob.uv_theorique)}</span>
                              <span className="text-gray-500">UV fermeture</span>
                              <span className="text-right font-medium">{formatMoney(ob.uv_fermeture)}</span>
                              <span className="text-gray-500">Écart UV</span>
                              <span className={`text-right font-bold ${ecart === 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {formatMoney(ob.ecart_uv)}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Historique des points */}
          {caisseHistory.length > 0 && (
            <div className="bg-white rounded-xl border overflow-hidden">
              <div className="px-4 py-3 border-b bg-gray-50">
                <span className="text-sm font-medium text-gray-700">Historique des points de caisse</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="px-4 py-2 text-left">Date</th>
                      <th className="px-4 py-2 text-right">Ouverture</th>
                      <th className="px-4 py-2 text-right">Fermeture</th>
                      <th className="px-4 py-2 text-right">Transactions</th>
                      <th className="px-4 py-2 text-right">Commissions</th>
                      <th className="px-4 py-2 text-right">Écart</th>
                      <th className="px-4 py-2 text-center">Statut</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {caisseHistory.map(c => (
                      <tr key={c.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2">
                          {new Date(c.date_point).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                        </td>
                        <td className="px-4 py-2 text-right">{formatMoney(c.solde_ouverture)}</td>
                        <td className="px-4 py-2 text-right">{c.solde_fermeture != null ? formatMoney(c.solde_fermeture) : '—'}</td>
                        <td className="px-4 py-2 text-right">{c.nombre_transactions}</td>
                        <td className="px-4 py-2 text-right text-green-600 font-medium">{formatMoney(c.total_commissions)}</td>
                        <td className={`px-4 py-2 text-right font-medium ${parseFloat(c.ecart) === 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {c.statut === 'ferme' ? formatMoney(c.ecart) : '—'}
                        </td>
                        <td className="px-4 py-2 text-center">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${c.statut === 'ferme' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                            {c.statut === 'ferme' ? 'Fermé' : 'Ouvert'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ========== TAB: COMMISSIONS ========== */}
      {tab === 'commissions' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-500">Configurez les barèmes de commission par opérateur et type de transaction.</p>
            <button onClick={() => setShowRateForm(!showRateForm)}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-indigo-700 flex items-center gap-2">
              <Plus size={16} />
              Ajouter un barème
            </button>
          </div>

          {/* Formulaire barème */}
          {showRateForm && (
            <form onSubmit={handleSaveRate} className="bg-white border-2 border-indigo-200 rounded-xl p-5 space-y-4">
              <h3 className="font-semibold text-gray-900">Nouveau barème de commission</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Opérateur *</label>
                  <select required value={rateForm.operator_id} onChange={e => setRateForm({...rateForm, operator_id: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2 text-sm">
                    <option value="">Sélectionner...</option>
                    {operators.filter(o => o.actif).map(o => <option key={o.id} value={o.id}>{o.nom}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type de transaction *</label>
                  <select required value={rateForm.type_transaction} onChange={e => setRateForm({...rateForm, type_transaction: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2 text-sm">
                    {TRANSACTION_TYPES.map(t => <option key={t} value={t}>{TYPE_LABELS[t].label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Montant min (F CFA)</label>
                  <input type="number" value={rateForm.montant_min} onChange={e => setRateForm({...rateForm, montant_min: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Montant max (F CFA)</label>
                  <input type="number" value={rateForm.montant_max} onChange={e => setRateForm({...rateForm, montant_max: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Commission fixe (F CFA)</label>
                  <input type="number" step="0.01" value={rateForm.commission_fixe} onChange={e => setRateForm({...rateForm, commission_fixe: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Commission (%)</label>
                  <input type="number" step="0.01" value={rateForm.commission_pourcentage} onChange={e => setRateForm({...rateForm, commission_pourcentage: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
              <div className="flex gap-3 justify-end">
                <button type="button" onClick={() => setShowRateForm(false)} className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50">Annuler</button>
                <button type="submit" className="px-5 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium">Enregistrer</button>
              </div>
            </form>
          )}

          {/* Liste des barèmes */}
          <div className="bg-white rounded-xl border overflow-hidden">
            {commissionRates.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Aucun barème configuré</p>
                <p className="text-xs mt-1">Les commissions seront à 0 par défaut</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="px-4 py-2 text-left">Opérateur</th>
                      <th className="px-4 py-2 text-left">Type</th>
                      <th className="px-4 py-2 text-right">Montant min</th>
                      <th className="px-4 py-2 text-right">Montant max</th>
                      <th className="px-4 py-2 text-right">Commission fixe</th>
                      <th className="px-4 py-2 text-right">Commission %</th>
                      <th className="px-4 py-2 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {commissionRates.map(rate => (
                      <tr key={rate.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 font-medium">{rate.operator_nom}</td>
                        <td className="px-4 py-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${TYPE_LABELS[rate.type_transaction]?.badge || 'bg-gray-100'}`}>
                            {TYPE_LABELS[rate.type_transaction]?.label || rate.type_transaction}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right">{formatMoney(rate.montant_min)}</td>
                        <td className="px-4 py-2 text-right">{formatMoney(rate.montant_max)}</td>
                        <td className="px-4 py-2 text-right font-medium">{formatMoney(rate.commission_fixe)}</td>
                        <td className="px-4 py-2 text-right">{rate.commission_pourcentage}%</td>
                        <td className="px-4 py-2 text-center">
                          <button onClick={() => handleDeleteRate(rate.id)} className="text-red-400 hover:text-red-600">
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========== TAB: BILAN ========== */}
      {tab === 'bilan' && (
        <div className="space-y-6">
          {/* Sélection de période */}
          <div className="bg-white rounded-xl border p-4">
            <div className="flex flex-col sm:flex-row items-end gap-3">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Date début</label>
                <input type="date" value={reportDates.date_debut}
                  onChange={e => setReportDates({...reportDates, date_debut: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Date fin</label>
                <input type="date" value={reportDates.date_fin}
                  onChange={e => setReportDates({...reportDates, date_fin: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div className="flex gap-2">
                {/* Raccourcis */}
                {[
                  { label: 'Aujourd\'hui', fn: () => { const d = new Date().toISOString().split('T')[0]; setReportDates({ date_debut: d, date_fin: d }); }},
                  { label: '7j', fn: () => { const f = new Date(); const d = new Date(f); d.setDate(d.getDate() - 6); setReportDates({ date_debut: d.toISOString().split('T')[0], date_fin: f.toISOString().split('T')[0] }); }},
                  { label: 'Ce mois', fn: () => { const n = new Date(); setReportDates({ date_debut: new Date(n.getFullYear(), n.getMonth(), 1).toISOString().split('T')[0], date_fin: n.toISOString().split('T')[0] }); }},
                  { label: 'Mois dernier', fn: () => { const n = new Date(); const d = new Date(n.getFullYear(), n.getMonth() - 1, 1); const f = new Date(n.getFullYear(), n.getMonth(), 0); setReportDates({ date_debut: d.toISOString().split('T')[0], date_fin: f.toISOString().split('T')[0] }); }},
                ].map(s => (
                  <button key={s.label} onClick={s.fn}
                    className="px-3 py-2 text-xs border rounded-lg text-gray-600 hover:bg-gray-100 whitespace-nowrap">
                    {s.label}
                  </button>
                ))}
              </div>
              <button onClick={loadReport} disabled={reportLoading}
                className="bg-indigo-600 text-white px-5 py-2 rounded-lg hover:bg-indigo-700 font-medium flex items-center gap-2 shrink-0 disabled:opacity-50">
                <BarChart3 size={16} />
                {reportLoading ? 'Chargement...' : 'Générer'}
              </button>
            </div>
          </div>

          {reportLoading && <DjamannLoader variant="section" text="Génération du bilan..." />}

          {report && !reportLoading && (
            <div id="bilan-print-zone">
              {/* En-tête entreprise (visible uniquement à l'impression) */}
              <div className="hidden print:block mb-6 pb-4" style={{ borderBottom: '2px solid #000' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    {settings?.logo && (
                      <img
                        src={settings.logo.startsWith('http') ? settings.logo : `${BACKEND_URL}${settings.logo}`}
                        alt="Logo"
                        style={{ width: '60px', height: '60px', objectFit: 'contain' }}
                      />
                    )}
                    <div>
                      <h2 style={{ fontSize: '18px', fontWeight: 'bold', margin: 0 }}>{settings?.nom_entreprise || 'Mon Entreprise'}</h2>
                      {settings?.adresse && <p style={{ fontSize: '12px', margin: '2px 0', color: '#555' }}>{settings.adresse}</p>}
                      {settings?.telephone && <p style={{ fontSize: '12px', margin: '2px 0', color: '#555' }}>Tél: {settings.telephone}</p>}
                      {settings?.email && <p style={{ fontSize: '12px', margin: '2px 0', color: '#555' }}>{settings.email}</p>}
                      {settings?.numero_registre && <p style={{ fontSize: '11px', margin: '2px 0', color: '#888' }}>RCCM: {settings.numero_registre}</p>}
                      {settings?.numero_fiscal && <p style={{ fontSize: '11px', margin: '2px 0', color: '#888' }}>NIF: {settings.numero_fiscal}</p>}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <h1 style={{ fontSize: '22px', fontWeight: 'bold', color: '#4F46E5', margin: 0 }}>BILAN MOBILE MONEY</h1>
                    <p style={{ fontSize: '13px', color: '#555', marginTop: '4px' }}>
                      Du {new Date(report.date_debut).toLocaleDateString('fr-FR')} au {new Date(report.date_fin).toLocaleDateString('fr-FR')}
                    </p>
                    <p style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
                      Généré le {new Date().toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
              </div>
              {/* Titre écran (masqué à l'impression) */}
              <div className="print:hidden text-center mb-4">
                <h2 className="text-lg font-bold text-gray-800">Bilan du {new Date(report.date_debut).toLocaleDateString('fr-FR')} au {new Date(report.date_fin).toLocaleDateString('fr-FR')}</h2>
              </div>

              {/* Bouton imprimer */}
              <div className="flex justify-end mb-4 print:hidden">
                <button onClick={() => {
                  const zone = document.getElementById('bilan-print-zone');
                  const original = document.body.innerHTML;
                  document.body.innerHTML = `<div style="padding: 15mm; font-family: sans-serif;">${zone.innerHTML}</div>`;
                  window.print();
                  document.body.innerHTML = original;
                  window.location.reload();
                }}
                  className="bg-gray-700 text-white px-4 py-2 rounded-lg text-sm hover:bg-gray-800 flex items-center gap-2">
                  <Printer size={16} />
                  Imprimer le bilan
                </button>
              </div>

              {/* KPI globaux */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 mb-6">
                <div className="bg-white rounded-xl border p-4">
                  <p className="text-xs text-gray-500">Transactions</p>
                  <p className="text-xl font-bold text-gray-900">{report.global.nombre_transactions}</p>
                </div>
                <div className="bg-white rounded-xl border p-4">
                  <p className="text-xs text-gray-500">Volume total</p>
                  <p className="text-xl font-bold text-indigo-700">{formatMoney(report.global.volume_total)}</p>
                </div>
                <div className="bg-white rounded-xl border p-4">
                  <p className="text-xs text-green-600">Commissions</p>
                  <p className="text-xl font-bold text-green-700">{formatMoney(report.global.total_commissions)}</p>
                </div>
                <div className="bg-white rounded-xl border p-4">
                  <p className="text-xs text-gray-500">Frais clients</p>
                  <p className="text-xl font-bold text-gray-700">{formatMoney(report.global.total_frais)}</p>
                </div>
                <div className="bg-white rounded-xl border p-4">
                  <p className="text-xs text-gray-500">Jours travaillés</p>
                  <p className="text-xl font-bold text-gray-900">{report.global.jours_travailles}</p>
                </div>
                <div className="bg-white rounded-xl border p-4">
                  <p className="text-xs text-gray-500">Comm./jour moy.</p>
                  <p className="text-xl font-bold text-green-700">
                    {report.global.jours_travailles > 0 
                      ? formatMoney(parseFloat(report.global.total_commissions) / report.global.jours_travailles)
                      : '—'}
                  </p>
                </div>
              </div>

              {/* Détail par type de transaction */}
              <div className="bg-white rounded-xl border overflow-hidden mb-6">
                <div className="px-4 py-3 border-b bg-gray-50">
                  <h3 className="text-sm font-semibold text-gray-700">Répartition par type de transaction</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="px-4 py-2 text-left">Type</th>
                        <th className="px-4 py-2 text-right">Nombre</th>
                        <th className="px-4 py-2 text-right">Volume</th>
                        <th className="px-4 py-2 text-right">Commissions</th>
                        <th className="px-4 py-2 text-right">Montant moyen</th>
                        <th className="px-4 py-2 text-right">Min</th>
                        <th className="px-4 py-2 text-right">Max</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {report.by_type.map(t => {
                        const info = TYPE_LABELS[t.type] || {};
                        return (
                          <tr key={t.type} className="hover:bg-gray-50">
                            <td className="px-4 py-2">
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${info.badge || 'bg-gray-100 text-gray-700'}`}>
                                {info.label || t.type}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-right font-medium">{t.nombre}</td>
                            <td className="px-4 py-2 text-right font-semibold">{formatMoney(t.total_montant)}</td>
                            <td className="px-4 py-2 text-right text-green-600 font-medium">{formatMoney(t.total_commissions)}</td>
                            <td className="px-4 py-2 text-right text-gray-600">{formatMoney(t.montant_moyen)}</td>
                            <td className="px-4 py-2 text-right text-gray-500 text-xs">{formatMoney(t.montant_min)}</td>
                            <td className="px-4 py-2 text-right text-gray-500 text-xs">{formatMoney(t.montant_max)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Détail par opérateur */}
              <div className="bg-white rounded-xl border overflow-hidden mb-6">
                <div className="px-4 py-3 border-b bg-gray-50">
                  <h3 className="text-sm font-semibold text-gray-700">Performance par opérateur</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="px-4 py-2 text-left">Opérateur</th>
                        <th className="px-4 py-2 text-right">Tx</th>
                        <th className="px-4 py-2 text-right">Dépôts</th>
                        <th className="px-4 py-2 text-right">Retraits</th>
                        <th className="px-4 py-2 text-right">Transferts</th>
                        <th className="px-4 py-2 text-right">Factures</th>
                        <th className="px-4 py-2 text-right">Crédits</th>
                        <th className="px-4 py-2 text-right">Commissions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {report.by_operator.map(op => (
                        <tr key={op.operator_id} className="hover:bg-gray-50">
                          <td className="px-4 py-2">
                            <span className="inline-flex items-center gap-2">
                              <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: op.couleur }}></span>
                              <span className="font-medium">{op.nom}</span>
                            </span>
                          </td>
                          <td className="px-4 py-2 text-right">{op.nombre_transactions}</td>
                          <td className="px-4 py-2 text-right">{formatMoney(op.total_depots)}</td>
                          <td className="px-4 py-2 text-right">{formatMoney(op.total_retraits)}</td>
                          <td className="px-4 py-2 text-right">{formatMoney(op.total_transferts)}</td>
                          <td className="px-4 py-2 text-right">{formatMoney(op.total_factures)}</td>
                          <td className="px-4 py-2 text-right">{formatMoney(op.total_credits)}</td>
                          <td className="px-4 py-2 text-right text-green-600 font-semibold">{formatMoney(op.total_commissions)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Activité par jour */}
              <div className="bg-white rounded-xl border overflow-hidden mb-6">
                <div className="px-4 py-3 border-b bg-gray-50">
                  <h3 className="text-sm font-semibold text-gray-700">Activité par jour</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="px-4 py-2 text-left">Date</th>
                        <th className="px-4 py-2 text-right">Tx</th>
                        <th className="px-4 py-2 text-right">Dépôts</th>
                        <th className="px-4 py-2 text-right">Retraits</th>
                        <th className="px-4 py-2 text-right">Autres</th>
                        <th className="px-4 py-2 text-right">Commissions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {report.by_day.map(d => {
                        const autres = parseFloat(d.total_transferts || 0) + parseFloat(d.total_factures || 0) + parseFloat(d.total_credits || 0);
                        return (
                          <tr key={d.date_jour} className="hover:bg-gray-50">
                            <td className="px-4 py-2 font-medium">
                              {new Date(d.date_jour).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
                            </td>
                            <td className="px-4 py-2 text-right">{d.nombre_transactions}</td>
                            <td className="px-4 py-2 text-right text-green-600">{formatMoney(d.total_depots)}</td>
                            <td className="px-4 py-2 text-right text-red-600">{formatMoney(d.total_retraits)}</td>
                            <td className="px-4 py-2 text-right text-blue-600">{formatMoney(autres)}</td>
                            <td className="px-4 py-2 text-right text-green-700 font-semibold">{formatMoney(d.total_commissions)}</td>
                          </tr>
                        );
                      })}
                      {/* Ligne totaux */}
                      <tr className="bg-gray-100 font-bold">
                        <td className="px-4 py-2">TOTAL</td>
                        <td className="px-4 py-2 text-right">{report.global.nombre_transactions}</td>
                        <td className="px-4 py-2 text-right text-green-600">{formatMoney(report.global.total_depots)}</td>
                        <td className="px-4 py-2 text-right text-red-600">{formatMoney(report.global.total_retraits)}</td>
                        <td className="px-4 py-2 text-right text-blue-600">
                          {formatMoney(parseFloat(report.global.total_transferts || 0) + parseFloat(report.global.total_factures || 0) + parseFloat(report.global.total_credits || 0))}
                        </td>
                        <td className="px-4 py-2 text-right text-green-700">{formatMoney(report.global.total_commissions)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Points de caisse de la période */}
              {report.closings && report.closings.length > 0 && (
                <div className="bg-white rounded-xl border overflow-hidden">
                  <div className="px-4 py-3 border-b bg-gray-50">
                    <h3 className="text-sm font-semibold text-gray-700">Récapitulatif des caisses</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-gray-600">
                        <tr>
                          <th className="px-4 py-2 text-left">Date</th>
                          <th className="px-4 py-2 text-right">Ouverture</th>
                          <th className="px-4 py-2 text-right">Fermeture</th>
                          <th className="px-4 py-2 text-right">Commissions</th>
                          <th className="px-4 py-2 text-right">Écart</th>
                          <th className="px-4 py-2 text-center">Statut</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {report.closings.map(c => (
                          <tr key={c.id} className="hover:bg-gray-50">
                            <td className="px-4 py-2 font-medium">
                              {new Date(c.date_point).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                            </td>
                            <td className="px-4 py-2 text-right">{formatMoney(c.solde_ouverture)}</td>
                            <td className="px-4 py-2 text-right">{c.solde_fermeture != null ? formatMoney(c.solde_fermeture) : '—'}</td>
                            <td className="px-4 py-2 text-right text-green-600">{formatMoney(c.total_commissions)}</td>
                            <td className={`px-4 py-2 text-right font-medium ${parseFloat(c.ecart) === 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {c.statut === 'ferme' ? formatMoney(c.ecart) : '—'}
                            </td>
                            <td className="px-4 py-2 text-center">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${c.statut === 'ferme' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                {c.statut === 'ferme' ? 'Fermé' : 'Ouvert'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>

    {/* Modal reçu Mobile Money */}
    {receiptTx && (
      <MobileMoneyReceipt transaction={receiptTx} onClose={() => setReceiptTx(null)} />
    )}
    </>
  );
};

// Composant stat card
const StatCard = ({ icon, label, value }) => (
  <div className="bg-white rounded-xl border p-4">
    <div className="flex items-center gap-2 mb-1">
      {icon}
      <span className="text-xs text-gray-500">{label}</span>
    </div>
    <p className="text-lg font-bold text-gray-900">{value}</p>
  </div>
);

export default BusinessMobileMoney;
